import React, {
  FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useStoreContext } from 'features/store';
import isEmpty from 'lodash/isEmpty';

import { IRestaurant } from '@rbi-ctg/store';
import { toaster } from 'components/toaster/toaster';
import { ServiceMode } from 'generated/graphql-gateway';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useSetResetCartTimeout } from 'hooks/use-set-reset-cart-timeout';
import { actions, useAppDispatch } from 'state/global-state';
import { removeAppliedRewardsInStorage } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useEvaluateLoyaltyAtRestaurant } from 'state/loyalty/hooks/use-evaluate-loyalty-at-restaurant';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { useServiceModeLive } from 'state/service-mode';
import { SyncStorageKey } from 'utils/environment/sync-storage';

import { IRewardAction, RewardRedemptionMethod } from '../hooks/types';
import { useLoyaltyUser } from '../hooks/use-loyalty-user';

import { ICartEntryType, useInRestaurantCart } from './hooks/use-in-restaurant-redemption-cart';
import type {
  ICreateCartEntryParams,
  ILegacyCartEntry,
} from './hooks/use-in-restaurant-redemption-cart';
import { ShortCodeState, useShortCode } from './hooks/use-short-code';
import { ShortCodePollingStatus } from './hooks/use-short-code-poll-status';
import { isRedemptionOnCurrentDevice } from './is-redemption-on-current-device';
import { isServiceModeValid } from './is-service-mode-valid';
import { ShortCodeOverrideModal } from './modals';
import {
  IInRestaurantFlagVariations,
  IInRestaurantRedemptionContext,
  InRestaurantRedemptionFlagsState,
} from './types';
import { generateOrderLineItems, getAppliedOffersFromInRestaurantCart } from './utils';

interface IOverrideModalConfirmation {
  overrideShortCodeCallback: () => void;
}

export const InRestaurantRedemptionContext = createContext<IInRestaurantRedemptionContext>(
  {} as IInRestaurantRedemptionContext
);
export const useInRestaurantRedemptionContext = () => useContext(InRestaurantRedemptionContext);

export const InRestaurantRedemptionProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { loyaltyUser } = useLoyaltyUser();

  const { store } = useStoreContext();
  const { evaluateLoyaltyAtRestaurant } = useEvaluateLoyaltyAtRestaurant();
  const serviceMode = useServiceModeLive();
  const isMobile = useIsMobileBreakpoint();
  const loyaltyUserId = loyaltyUser?.id;

  const [
    ShortCodeOverrideModalConfirmation,
    openOverrideModalConfirmation,
  ] = (useDialogModal as any)({
    Component: ShortCodeOverrideModal,
    onConfirm: ({ overrideShortCodeCallback }: IOverrideModalConfirmation) => {
      overrideShortCodeCallback();
    },
  });
  const shouldForceRestaurantSelection = Boolean(
    useFlag(LaunchDarklyFlag.FORCE_RESTAURANT_SELECTION_FOR_REWARDS)
  );

  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const rewardRedemptionLimit = useRewardRedemptionLimit();
  const isIdAndPayFlagEnabled = useFlag(LaunchDarklyFlag.ENABLE_ID_AND_PAY_FE);

  const enableInRestaurantVariations = useFlag<IInRestaurantFlagVariations>(
    LaunchDarklyFlag.ENABLE_IN_RESTAURANT_VARIATIONS
  );
  const enableInRestaurantRedemption = !isEmpty(enableInRestaurantVariations);
  const inRestaurantRedemptionEnabled = Boolean(
    isRedemptionOnCurrentDevice({ isMobile }) &&
      enableInRestaurantRedemption &&
      isServiceModeValid({ serviceMode })
  );

  const [
    { inRestaurantLoyaltyEnabledAtRestaurant, inRestaurantLoyaltyEnabledAtRestaurantLoading },
    setFlagsConfig,
  ] = useState<InRestaurantRedemptionFlagsState>({
    inRestaurantLoyaltyEnabledAtRestaurantLoading: true,
    inRestaurantLoyaltyEnabledAtRestaurant: inRestaurantRedemptionEnabled,
  });
  const isLoadingRef = useRef(inRestaurantLoyaltyEnabledAtRestaurantLoading);
  isLoadingRef.current = inRestaurantLoyaltyEnabledAtRestaurantLoading;

  const [isQuickPayEnabled, setIsQuickPayEnabled] = useState(false);
  const [shouldResetPollingResultStatus, setShouldResetPollingResultStatus] = useState(false);

  const [pollingShortCodeStatus, setPollingShortCodeStatus] = useState<ShortCodePollingStatus>(
    ShortCodePollingStatus.None
  );
  const [scanSucceeded, setScanSucceeded] = useState<boolean>(false);

  const setScanSuccess = (success: boolean) => {
    setScanSucceeded(success);
  };

  /** Evaluates InRestaurantRedemption feature availability against a store different from the one set in context */
  const isInRestaurantRedemptionEnabledAtStore = useCallback(
    async (storeToCheck: IRestaurant, serviceModeToCheck?: ServiceMode | null) => {
      if (!inRestaurantRedemptionEnabled) {
        return false;
      }

      return shouldForceRestaurantSelection
        ? await evaluateLoyaltyAtRestaurant(storeToCheck, serviceModeToCheck)
        : true;
    },
    [evaluateLoyaltyAtRestaurant, inRestaurantRedemptionEnabled, shouldForceRestaurantSelection]
  );

  /** Evaluates restaurant loyalty ability against the current Store in the context */
  useEffect(() => {
    let mounted = true;

    async function setEnabledAtRestaurant() {
      setFlagsConfig(prev =>
        prev.inRestaurantLoyaltyEnabledAtRestaurantLoading
          ? prev
          : { ...prev, inRestaurantLoyaltyEnabledAtRestaurantLoading: true }
      );
      let isEnabledAtRestaurant = false;

      if (store._id) {
        isEnabledAtRestaurant = await isInRestaurantRedemptionEnabledAtStore(store, serviceMode);
      }

      if (mounted) {
        setFlagsConfig({
          inRestaurantLoyaltyEnabledAtRestaurant: isEnabledAtRestaurant,
          inRestaurantLoyaltyEnabledAtRestaurantLoading: false,
        });
      }
    }

    setEnabledAtRestaurant();

    return () => {
      mounted = false;
    };
  }, [isInRestaurantRedemptionEnabledAtStore, serviceMode, store]);

  const dispatch = useAppDispatch();

  const {
    inRestaurantCart,
    addInRestaurantCartEntry,
    removeInRestaurantCartEntry: removeInRestaurantRedemption,
    updateInRestaurantCartEntryQuantity: updateInRestaurantRedemptionQuantity,
    resetInRestaurantCart,
    isInRestaurantCartEmpty,
    existEntryTypeInRestaurantCart,
    clearInRestaurantCartAllRewards,
    removeTypeFromCart,
  } = useInRestaurantCart();

  const {
    resetShortCode,
    getNewShortCode,
    shortCodeLoading,
    shortCodeState,
    setShortCodeClaimed,
    setShortCodeExpired,
    shortCode,
  } = useShortCode();

  const clearInRestaurantCartAllOffers = useCallback(() => {
    removeTypeFromCart(ICartEntryType.OFFER);
    removeTypeFromCart(ICartEntryType.LEGACY_OFFER);
  }, [removeTypeFromCart]);

  const storeNumber = store?.number;

  const resetInRestaurantRedemption = useCallback(() => {
    // reset all the in-restaurant state
    setShouldResetPollingResultStatus(true);
    resetShortCode();
    resetInRestaurantCart();

    // reset loyalty state
    removeAppliedRewardsInStorage();
    dispatch(
      actions.loyalty.resetLoyaltyRewardsState({
        points: loyaltyUser?.points ?? 0,
        shouldResetAvailableRewardsMap: false,
        // Explicitly sent to reset ONLY the in restaurant rewards
        redemptionMethod: RewardRedemptionMethod.IN_RESTAURANT,
      })
    );
  }, [dispatch, loyaltyUser, resetInRestaurantCart, resetShortCode]);

  const generateShortCode = useCallback(() => {
    if (loyaltyUserId) {
      getNewShortCode({
        appliedOffers: getAppliedOffersFromInRestaurantCart(inRestaurantCart),
        inRestaurantOrder: generateOrderLineItems(inRestaurantCart),
        loyaltyId: loyaltyUserId,
        restaurantId: storeNumber,
        serviceMode,
      });
    }
  }, [getNewShortCode, inRestaurantCart, loyaltyUserId, serviceMode, storeNumber]);

  useEffectOnUpdates(() => {
    resetShortCode();
    // generate a new short code on every cart update
    generateShortCode();
  }, [inRestaurantCart]);

  useEffect(() => {
    // TODO: I changed this code with a compatible logic, but I'm not sure if the previous logic was correct

    // We are not using `inRestaurantLoyaltyEnabledAtRestaurantLoading` directly
    // because it will re-trigger a state reset anytime a Store changes
    if (isLoadingRef.current) {
      return;
    }

    // Reset in restaurant redemption if the feature isn't enabled
    if (!inRestaurantRedemptionEnabled || !inRestaurantLoyaltyEnabledAtRestaurant) {
      resetInRestaurantRedemption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inRestaurantRedemptionEnabled, inRestaurantLoyaltyEnabledAtRestaurant]);

  // TODO: I'm not sure if this is the correct place to put this logic
  const isIdAndPayEnabled = isIdAndPayFlagEnabled && loyaltyUser?.idPay?.enabled;

  useEffect(() => {
    if (shortCodeState === ShortCodeState.OfferValidationError) {
      toaster.neutral(t('invalidOffersRemoved'));

      // TODO: clear only invalid offers
      clearInRestaurantCartAllOffers();
    }
  }, [clearInRestaurantCartAllOffers, shortCodeState, isIdAndPayEnabled]);

  const { enableOfferRedemption = false, enableRewardRedemption = false } =
    enableInRestaurantVariations || {};
  const canRedeemLoyaltyIncentives = enableOfferRedemption || enableRewardRedemption;

  useEffect(() => {
    if (!enableOfferRedemption) {
      clearInRestaurantCartAllOffers();
    }
    if (!enableRewardRedemption) {
      clearInRestaurantCartAllRewards();
    }
  }, [
    clearInRestaurantCartAllOffers,
    clearInRestaurantCartAllRewards,
    enableOfferRedemption,
    enableRewardRedemption,
    removeTypeFromCart,
  ]);
  /**
   * Adds the redemption object to the cart and applies the reward if necessary,
   * and displays a toast if the action was successful, and redirects the user
   * to the in-restaurant redemption page.
   */
  const addInRestaurantRedemptionEntry = useCallback(
    (item: ICreateCartEntryParams) => {
      const {
        entry: cartEntry,
        evictedRewardEntries,
        decrementedRewardEntries,
      } = addInRestaurantCartEntry(item);

      evictedRewardEntries.forEach(evicted => {
        const { engineReward } = evicted.details;
        dispatch(
          actions.loyalty.removeAppliedReward({
            cartId: engineReward.id,
            rewardBenefitId: engineReward.rewardBenefitId,
          })
        );
      });

      decrementedRewardEntries.forEach(decremented => {
        const { engineReward } = decremented.details;
        dispatch(
          actions.loyalty.unApplyReward({
            cartId: engineReward.id,
            rewardBenefitId: engineReward.rewardBenefitId,
            ...(loyaltyUser ? { loyaltyUser } : {}),
          })
        );
      });

      if (cartEntry) {
        let entityType = 'offer';

        if (cartEntry.type === ICartEntryType.REWARD) {
          entityType = 'reward';
          // apply the reward
          const { engineReward } = cartEntry.details;
          dispatch(
            actions.loyalty.applyReward({
              cartId: engineReward.id,
              rewardBenefitId: engineReward.rewardBenefitId,
              redemptionMethod: RewardRedemptionMethod.IN_RESTAURANT,
              enableRewardUnstacking,
              rewardRedemptionLimit,
            })
          );
        }

        const entityTypeTranslationKey: TranslationKey =
          entityType === 'reward' ? 'reward' : 'offer';

        toaster.positive(
          t('attachedToMyCodeSuccess', {
            type: t(entityTypeTranslationKey),
          })
        );
      }
    },
    [addInRestaurantCartEntry, dispatch, enableRewardUnstacking, loyaltyUser, rewardRedemptionLimit]
  );

  /**
   * Removes the cart entry from the cart and remove the reward from `applied rewards` if necessary.
   */
  const removeInRestaurantRedemptionEntry = useCallback(
    (cartEntry: ILegacyCartEntry) => {
      removeInRestaurantRedemption(cartEntry);
      if (cartEntry.type === ICartEntryType.REWARD) {
        const { engineReward } = cartEntry.details;

        // remove the applied reward
        dispatch(
          actions.loyalty.removeAppliedReward({
            cartId: engineReward.id,
            rewardBenefitId: engineReward.rewardBenefitId,
          })
        );
      }
    },
    [dispatch, removeInRestaurantRedemption]
  );

  /**
   * Updates the cart entry quantity and apply or unapply the reward depending on the given and previous quantity.
   */
  const updateInRestaurantRedemptionEntryQuantity = useCallback(
    (cartEntry: ILegacyCartEntry, quantity: number) => {
      updateInRestaurantRedemptionQuantity(cartEntry, quantity);

      // apply or un-apply the reward as many times as necessary
      if (cartEntry.type === ICartEntryType.REWARD) {
        const {
          details: { engineReward },
          quantity: beforeQuantity,
        } = cartEntry;
        const { applyReward, unApplyReward } = actions.loyalty;
        const isDecrementAction = quantity - beforeQuantity < 0;

        const { id: cartId, rewardBenefitId } = engineReward;
        for (let i = 0; i < Math.abs(quantity - beforeQuantity); i++) {
          const basePayload: IRewardAction & { loyaltyUser?: typeof loyaltyUser } = {
            rewardBenefitId,
            cartId,
            ...(loyaltyUser ? { loyaltyUser } : {}),
          };

          if (isDecrementAction) {
            dispatch(unApplyReward(basePayload));
          } else {
            dispatch(
              applyReward({
                ...basePayload,
                redemptionMethod: RewardRedemptionMethod.IN_RESTAURANT,
                enableRewardUnstacking,
                rewardRedemptionLimit,
              })
            );
          }
        }
      }
    },
    [
      dispatch,
      loyaltyUser,
      updateInRestaurantRedemptionQuantity,
      enableRewardUnstacking,
      rewardRedemptionLimit,
    ]
  );

  /**
   * The actions that have the ability to override the short code, should be wrapper by
   * this function in order to display a confirmation modal before the action is called.
   */
  const overrideShortCodeActionWrapper = useCallback(
    <T extends (...args: any) => void>(callback: T): ((...args: Parameters<T>) => void) => (
      ...args: Parameters<T>
    ) => {
      if (pollingShortCodeStatus === ShortCodePollingStatus.Processing) {
        // call to the callback to override the state
        openOverrideModalConfirmation({
          overrideShortCodeCallback: () => callback(...args),
        });
      } else {
        callback(...args);
      }
    },
    [openOverrideModalConfirmation, pollingShortCodeStatus]
  );

  useSetResetCartTimeout({
    storageKey: SyncStorageKey.IN_RESTAURANT_CART_LAST_UPDATE,
    cart: inRestaurantCart,
    resetCartCallback: resetInRestaurantRedemption,
  });

  return (
    <InRestaurantRedemptionContext.Provider
      value={{
        enableOfferRedemption,
        enableRewardRedemption,
        inRestaurantLoyaltyEnabledAtRestaurant,
        inRestaurantLoyaltyEnabledAtRestaurantLoading,
        inRestaurantRedemptionEnabled,

        isQuickPayEnabled,
        setIsQuickPayEnabled,

        shortCodeLoading,
        shortCodeState,
        shortCode,
        generateShortCode,
        setShortCodeClaimed,
        setShortCodeExpired,

        pollingShortCodeStatus,
        setPollingShortCodeStatus,

        shouldResetPollingResultStatus,
        setShouldResetPollingResultStatus,

        inRestaurantRedemptionCart: inRestaurantCart,
        // applying the override short code wrapper to catch up when the cart is changing
        // and display a confirmation modal
        addInRestaurantRedemptionEntry: overrideShortCodeActionWrapper<
          typeof addInRestaurantRedemptionEntry
        >(addInRestaurantRedemptionEntry),
        removeInRestaurantRedemptionEntry: overrideShortCodeActionWrapper<
          typeof removeInRestaurantRedemptionEntry
        >(removeInRestaurantRedemptionEntry),
        updateInRestaurantRedemptionEntryQuantity: overrideShortCodeActionWrapper<
          typeof updateInRestaurantRedemptionEntryQuantity
        >(updateInRestaurantRedemptionEntryQuantity),
        isInRestaurantRedemptionCartEmpty: isInRestaurantCartEmpty,
        resetInRestaurantRedemption,

        existEntryTypeInRestaurantRedemptionCart: existEntryTypeInRestaurantCart,
        clearInRestaurantRedemptionAllRewards: clearInRestaurantCartAllRewards,
        isInRestaurantRedemptionEnabledAtStore,
        scanSucceeded,
        setScanSuccess,
        shouldForceRestaurantSelection,
        canRedeemLoyaltyIncentives,
      }}
    >
      <ShortCodeOverrideModalConfirmation />

      {children}
    </InRestaurantRedemptionContext.Provider>
  );
};
